import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { AsyncSelect, SingleValue } from 'chakra-react-select';
import { ReactNode, useCallback, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
type Option = {
  label: ReactNode;
  value: string;
  isDisabled?: boolean;
};

type CourseItemForSupersession = {
  id: string;
  unitOfCompetency?: { title: string } | null;
};

type CourseItemForSupersessionList = {
  unit_of_competency_id: string | null;
};

export function CourseItemMarkSupersededModal({
  isOpen,
  onClose,
  courseItem,
  courseId,
  allCourseItems,
}: {
  isOpen: boolean;
  onClose: () => void;
  courseItem: CourseItemForSupersession;
  courseId: string;
  allCourseItems: CourseItemForSupersessionList[];
}) {
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const toast = useToast();
  const trpcUtils = trpcAdmin.useUtils();

  const markAsSupersededMutation = trpcAdmin.courses.markCourseItemAsSuperseded.useMutation({
    onSuccess() {
      toast({
        status: 'success',
        title: t`Unit marked as superseded successfully`,
      });
      void trpcUtils.courses.byId.invalidate(courseId);
      handleClose();
    },
    onError(error) {
      toast({
        status: 'error',
        title: error?.message ?? t`An error occurred while superseding the unit.`,
      });
    },
  });

  const handleClose = useCallback(() => {
    setSelectedUnitId(null);
    setNote('');
    onClose();
  }, [onClose]);

  const existingUnitIds = new Set(
    allCourseItems
      .map((courseItem) => courseItem.unit_of_competency_id)
      .filter((id) => id !== null),
  );

  const debouncedLoadOptions = useDebounceCallback(
    async (inputValue: string, callback: (options: Option[]) => void) => {
      const data = await trpcUtils.unitsOfCompetency.find.fetch(inputValue);

      const options = data.map(
        (unit) =>
          ({
            value: unit.id,
            isDisabled: existingUnitIds.has(unit.id),
            label: (
              <Stack gap={0}>
                <Text fontWeight="medium">{unit.title}</Text>
                <Text fontSize="sm" color="fg.muted">
                  {unit.code}
                  {unit.unitContextCategory?.categoryName
                    ? ` · ${unit.unitContextCategory.categoryName}`
                    : ''}
                </Text>
              </Stack>
            ),
          }) satisfies Option,
      );
      callback(options);
      return Promise.resolve(options);
    },
    300,
  );

  const handleChangeValue = useCallback((selectedOption: SingleValue<Option>) => {
    if (selectedOption?.value != null) {
      setSelectedUnitId(selectedOption.value);
    } else {
      setSelectedUnitId(null);
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedUnitId) {
      return;
    }
    markAsSupersededMutation.mutate({
      courseItemId: courseItem.id,
      supersededByUnitId: selectedUnitId,
      note,
    });
  }, [courseItem.id, selectedUnitId, note, markAsSupersededMutation]);

  return (
    <Modal onClose={handleClose} isOpen={isOpen} isCentered size="xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Mark unit as superseded</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack gap={4}>
            <Alert status="warning">
              <AlertIcon />
              <AlertDescription>
                <Trans>
                  This action is irreversible. The unit{' '}
                  <Box as="span" fontWeight="600">
                    {courseItem.unitOfCompetency?.title}
                  </Box>{' '}
                  will be marked as superseded within this course and cannot be reactivated.
                </Trans>
              </AlertDescription>
            </Alert>

            <FormControl isRequired>
              <FormLabel>
                <Trans>Replacement unit</Trans>
              </FormLabel>
              <AsyncSelect
                name="supersededByUnitId"
                placeholder={t`Search for a replacement unit`}
                defaultOptions
                loadOptions={debouncedLoadOptions}
                onChange={handleChangeValue}
                isClearable
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>
                <Trans>Notes</Trans>
              </FormLabel>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t`Add a note about this supersession`}
                rows={3}
              />
            </FormControl>
          </Stack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={handleClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            colorScheme="brand"
            isDisabled={!selectedUnitId || !note.trim()}
            isLoading={markAsSupersededMutation.isPending}
            onClick={handleSubmit}
          >
            <Trans>Mark as superseded</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
